/* Research Import Modal — AI-powered import (Excel/PDF/PNG/WeChat screenshot).
 * Akış: dosya yükle → AI analizi → önizleme tablosu → kullanıcı atla/dahil et → commit
 */
import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    X, Upload, Loader2, CheckCircle2, AlertTriangle, Sparkles, FileText, Plus, Link2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const fmtEur = (n) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n || 0);

function getHeaders() {
    const slug = sessionStorage.getItem('research_share_slug');
    const token = sessionStorage.getItem('research_share_token');
    if (slug && token) return { 'X-Share-Slug': slug, 'X-Share-Token': token };
    return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

const ACTION_LABEL = {
    match_variant: { txt: 'Eşleşti', cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
    new_variant:   { txt: 'Yeni Varyant', cls: 'bg-amber-100 text-amber-700', icon: Plus },
    new_product:   { txt: 'Yeni Ürün', cls: 'bg-indigo-100 text-indigo-700', icon: Sparkles },
};

export default function ImportModal({ onClose, onCommitted }) {
    const fileRef = useRef(null);
    const [file, setFile] = useState(null);
    const [phase, setPhase] = useState('select'); // select | analyzing | review | committing | done
    const [importData, setImportData] = useState(null); // {import_id, rows, inventory}
    const [excluded, setExcluded] = useState({}); // {row_index: true}
    const [stats, setStats] = useState(null);

    const upload = async () => {
        if (!file) { toast.error('Dosya seç'); return; }
        setPhase('analyzing');
        try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await axios.post(`${API}/api/research/import/upload`, fd, {
                headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
                timeout: 120000,
            });
            setImportData(r.data);
            setPhase('review');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'AI analizi başarısız');
            setPhase('select');
        }
    };

    const toggleRow = (idx) => setExcluded((p) => ({ ...p, [idx]: !p[idx] }));

    const commit = async () => {
        setPhase('committing');
        try {
            const rows = importData.rows.map((r) => ({
                row_index: r.row_index,
                include: !excluded[r.row_index],
            }));
            const res = await axios.post(`${API}/api/research/import/${importData.import_id}/commit`,
                { rows }, { headers: getHeaders(), timeout: 60000 });
            setStats(res.data.stats);
            setPhase('done');
            toast.success('Kaydedildi');
            onCommitted?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Kaydetme hatası');
            setPhase('review');
        }
    };

    const incl = importData?.rows.filter((r) => !excluded[r.row_index]).length || 0;
    const excl = importData?.rows.length ? importData.rows.length - incl : 0;

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white w-full max-w-4xl rounded-2xl max-h-[92vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()} data-testid="import-modal">
                {/* Header */}
                <div className="px-5 py-3 border-b flex items-center gap-3 bg-gradient-to-r from-indigo-50 to-white">
                    <Sparkles size={18} className="text-indigo-600" />
                    <div className="flex-1">
                        <h2 className="font-bold text-gray-900">AI ile İçeri Aktar</h2>
                        <p className="text-[11px] text-gray-500">Excel, PDF, PNG, WeChat ekran görüntüsü — AI okuyup eşleştirir</p>
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X size={20} /></button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-5">
                    {phase === 'select' && (
                        <div
                            onClick={() => fileRef.current?.click()}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setFile(f); }}
                            className="border-2 border-dashed border-indigo-300 bg-indigo-50/50 rounded-xl p-10 text-center cursor-pointer hover:bg-indigo-50">
                            <Upload size={32} className="mx-auto text-indigo-500" />
                            <div className="mt-3 font-bold text-gray-900">Dosyayı sürükle veya tıkla</div>
                            <div className="text-xs text-gray-500 mt-1">Excel, PDF, PNG, JPG, WebP — max 15MB</div>
                            {file && (
                                <div className="mt-4 inline-flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm">
                                    <FileText size={14} className="text-indigo-600" />
                                    <span className="font-semibold">{file.name}</span>
                                    <span className="text-[11px] text-gray-500">({(file.size / 1024).toFixed(0)} KB)</span>
                                </div>
                            )}
                            <input ref={fileRef} type="file" accept=".xlsx,.xls,.pdf,.png,.jpg,.jpeg,.webp,image/*,application/pdf"
                                onChange={(e) => setFile(e.target.files?.[0])} className="hidden" data-testid="import-file-input" />
                        </div>
                    )}

                    {phase === 'analyzing' && (
                        <div className="text-center py-16">
                            <Loader2 size={36} className="animate-spin text-indigo-500 mx-auto" />
                            <div className="mt-4 font-bold text-gray-900">AI dosyayı okuyor...</div>
                            <div className="text-xs text-gray-500 mt-1">Görüntü taranıyor → satırlar çıkarılıyor → mevcut envanter ile eşleştiriliyor (10–30 sn)</div>
                        </div>
                    )}

                    {phase === 'review' && importData && (
                        <PreviewTable
                            data={importData}
                            excluded={excluded}
                            onToggle={toggleRow}
                        />
                    )}

                    {phase === 'committing' && (
                        <div className="text-center py-16">
                            <Loader2 size={36} className="animate-spin text-indigo-500 mx-auto" />
                            <div className="mt-4 font-bold text-gray-900">Kaydediliyor...</div>
                        </div>
                    )}

                    {phase === 'done' && stats && (
                        <div className="text-center py-12">
                            <CheckCircle2 size={48} className="text-emerald-500 mx-auto" />
                            <div className="mt-3 font-bold text-lg text-gray-900">İçeri Aktarma Tamamlandı</div>
                            <div className="mt-4 grid grid-cols-4 gap-3 max-w-md mx-auto text-sm">
                                <Stat label="Yeni Ürün" value={stats.products} color="text-indigo-700" />
                                <Stat label="Yeni Varyant" value={stats.variants} color="text-amber-700" />
                                <Stat label="Tedarikçi" value={stats.suppliers} color="text-emerald-700" />
                                <Stat label="Birleşti" value={stats.merged} color="text-gray-500" />
                            </div>
                            <button onClick={onClose} className="mt-6 h-10 px-6 bg-indigo-600 text-white font-bold rounded-md" data-testid="import-close">
                                Kapat
                            </button>
                        </div>
                    )}
                </div>

                {/* Footer */}
                {phase === 'select' && (
                    <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2">
                        <button onClick={onClose} className="h-10 px-4 text-sm font-bold text-gray-700 bg-white border border-gray-300 rounded">İptal</button>
                        <button onClick={upload} disabled={!file}
                            className="h-10 px-4 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded inline-flex items-center gap-1"
                            data-testid="import-analyze-btn">
                            <Sparkles size={14} /> AI Analizi Başlat
                        </button>
                    </div>
                )}
                {phase === 'review' && (
                    <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-between">
                        <div className="text-xs text-gray-600">
                            <strong className="text-emerald-700">{incl}</strong> satır dahil edilecek
                            {excl > 0 && <span className="text-rose-600 ml-2"><strong>{excl}</strong> atlanacak</span>}
                        </div>
                        <div className="flex gap-2">
                            <button onClick={onClose} className="h-10 px-4 text-sm font-bold text-gray-700 bg-white border border-gray-300 rounded">İptal</button>
                            <button onClick={commit} disabled={incl === 0}
                                className="h-10 px-4 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded inline-flex items-center gap-1"
                                data-testid="import-commit-btn">
                                <CheckCircle2 size={14} /> Onayla ve Kaydet
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function Stat({ label, value, color }) {
    return (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <div className={`text-2xl font-bold ${color}`}>{value}</div>
            <div className="text-[10px] uppercase font-bold text-gray-500 mt-1">{label}</div>
        </div>
    );
}

function PreviewTable({ data, excluded, onToggle }) {
    const invByPid = Object.fromEntries((data.inventory || []).map((p) => [p.id, p]));

    return (
        <div className="space-y-2">
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
                <AlertTriangle size={12} className="inline mr-1" />
                AI kararlarını gözden geçir. İstemediğin satırları &quot;Atla&quot; ile devre dışı bırak.
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="bg-gray-100 text-gray-600">
                        <tr>
                            <th className="px-2 py-1.5 text-left">#</th>
                            <th className="px-2 py-1.5 text-left">Ürün / Varyant</th>
                            <th className="px-2 py-1.5 text-left">Tedarikçi</th>
                            <th className="px-2 py-1.5 text-right">Fiyat</th>
                            <th className="px-2 py-1.5 text-left">AI Kararı</th>
                            <th className="px-2 py-1.5 text-center">Dahil</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {data.rows.map((r) => {
                            const isExc = excluded[r.row_index];
                            const meta = ACTION_LABEL[r.ai_action] || ACTION_LABEL.new_product;
                            const IconEl = meta.icon;
                            const matchedProduct = r.ai_product_id ? invByPid[r.ai_product_id] : null;
                            const matchedProductName = matchedProduct?.name || r.ai_new_product_name || r.product_name_raw;
                            const matchedVariantName = (() => {
                                if (r.ai_variant_id && matchedProduct) {
                                    const v = (matchedProduct.variants || []).find((x) => x.id === r.ai_variant_id);
                                    if (v) return v.name;
                                }
                                return r.ai_new_variant_name || r.variant_raw || '-';
                            })();
                            return (
                                <tr key={r.row_index} className={isExc ? 'opacity-40 bg-rose-50/20' : 'hover:bg-gray-50'}>
                                    <td className="px-2 py-2 text-gray-500">{r.row_index + 1}</td>
                                    <td className="px-2 py-2">
                                        <div className="font-bold text-gray-900">{matchedProductName}</div>
                                        <div className="text-[10px] text-gray-500 flex items-center gap-1">
                                            <Link2 size={9} /> {matchedVariantName}
                                        </div>
                                    </td>
                                    <td className="px-2 py-2">
                                        <div className="font-semibold text-gray-800">{r.supplier_name}</div>
                                        {r.country && <div className="text-[10px] text-gray-500">{r.country}{r.moq > 0 && ` · MOQ ${r.moq}`}</div>}
                                    </td>
                                    <td className="px-2 py-2 text-right">
                                        <div className="font-bold text-gray-900">
                                            {r.currency === 'CNY' && '¥'}
                                            {r.currency === 'USD' && '$'}
                                            {r.currency === 'EUR' && '€'}
                                            {r.price.toFixed(2)}
                                        </div>
                                        <div className="text-[10px] text-gray-500">≈ {fmtEur(r.price_eur_normalized)}</div>
                                    </td>
                                    <td className="px-2 py-2">
                                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${meta.cls}`}>
                                            <IconEl size={10} /> {meta.txt}
                                        </span>
                                        {r.ai_reason && (
                                            <div className="text-[10px] text-gray-500 mt-0.5 line-clamp-2 max-w-[200px]">{r.ai_reason}</div>
                                        )}
                                    </td>
                                    <td className="px-2 py-2 text-center">
                                        <button onClick={() => onToggle(r.row_index)}
                                            className={`h-6 px-2 text-[10px] font-bold rounded ${isExc ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}
                                            data-testid={`toggle-row-${r.row_index}`}>
                                            {isExc ? 'Atla' : 'Dahil'}
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
